import React, { useState, useEffect } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { ContactType } from './contact-type';
import { hasDataDiscrepancy } from './has-data-discrepancy';
import { MatchingContactRow } from './matching-contact-row';
import { SimpleContactComparisonDialog } from './simple-contact-comparison-dialog';

import { employeeNameFromSimpleContact } from '../../../employees/utils/employee-name';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { timeoutPromise } from '../../../util/timeout-promise';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

interface InputProps {
  employeeDirectoryImportId: number;
  onNext: () => void;
  onCancel: () => void;
  PageIndicator: any;

  pathSegment: string;
  title: string;
  description: string;
}

type Props = InputProps;

export const EmployeeDirectoryImportWizardMatchingContactsBase = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  pathSegment,
  title,
  description,
  PageIndicator,
}: Props) => {
  const [matchingPeopleData, setMatchingPeopleData] = useState<{
    [importId: string]: PartiallyMatchingEmployeeDirectoryImportRow;
  } | null>(null);
  const [rowToResolve, setRowToResolve] = useState<{
    importId: number;
    existingData: PartiallyMatchingSimpleContact;
    importedData: EmployeeDirectoryImportSimpleContact;
    context: any;
  } | null>(null);
  const [resolutions, setResolutions] = useState<PartiallyMatchingContactResolutions>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [limitRows, setLimitRows] = useState<boolean>(true);
  const [shouldForceRehire, setShouldForceRehire] = useState(false);

  useEffect(() => {
    if (!hasInitialized) {
      const getMatchingContacts = () =>
        Api.get(`/employee-directory-import/${employeeDirectoryImportId}/${pathSegment}`)
          .then((resp) => {
            resp
              .json()
              .then((data: { [importId: string]: PartiallyMatchingEmployeeDirectoryImportRow }) => {
                if (data.errorMessage || typeof data !== 'object') {
                  setErrorMessage('Failed to load data');
                } else if (Object.keys(data).length === 0) {
                  onNext();
                } else {
                  setMatchingPeopleData(data);
                }
              });
          })
          .catch(() => {
            setErrorMessage('Failed to load data');
          });

      timeoutPromise(getMatchingContacts, () =>
        setErrorMessage('Request to get matching contacts timed out'),
      );

      setHasInitialized(true);
    }
  }, [hasInitialized, employeeDirectoryImportId, pathSegment, onNext]);

  const handleRadioButtonSelected =
    (
      importId: number,
      existingData: PartiallyMatchingSimpleContact | null,
      selectedContactId: number | null,
      createNewEntry: boolean,
    ) =>
    () => {
      setResolutions({
        ...resolutions,
        [importId]: {
          conflictResolution: null,
          selectedContactId,
          createNewEntry,
          context: existingData
            ? {
                employeeId: existingData.employeeId,
                jobApplicantId: existingData.jobApplicantId,
                referralId: existingData.referralId,
              }
            : null,
        },
      });
    };

  const handleResolveDiscrepancy =
    (
      importId: number,
      importedData: EmployeeDirectoryImportSimpleContact,
      existingData: PartiallyMatchingSimpleContact,
    ) =>
    () => {
      setRowToResolve({
        importId,
        importedData,
        existingData,
        context: {
          employeeId: existingData.employeeId,
          jobApplicantId: existingData.jobApplicantId,
          referralId: existingData.referralId,
        },
      });
    };

  const handleOnNext = async () => {
    let response = null;
    setIsSaving(true);
    try {
      response = await Api.post(
        `/employee-directory-import/${employeeDirectoryImportId}/${pathSegment}`,
        {
          body: JSON.stringify({ resolutions, shouldForceRehire }),
        },
      );
    } catch (e) {
      setErrorMessage('There was an error saving data');
    }
    const result = await response?.json();
    setIsSaving(false);
    if (!result || result.errorMessage) {
      setErrorMessage('There was an error saving data');
    } else {
      onNext();
    }
  };

  const closeResolveDiscrepancyDialog = (
    conflictResolution?: EmployeeDirectoryImportSimpleContact,
  ) => {
    if (conflictResolution && rowToResolve) {
      setResolutions({
        ...resolutions,
        [rowToResolve.importId]: {
          ...resolutions[rowToResolve.importId],
          conflictResolution,
        },
      });
    }
    setRowToResolve(null);
  };

  const clearResolution = (importId: number) => () => {
    const newResolutions = {
      ...resolutions,
    };
    delete newResolutions[importId];
    setResolutions(newResolutions);
  };

  const handleClickViewMoreRows = () => setLimitRows(false);

  const handleForceRehireCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldForceRehire(event.target.checked);
  };

  const nonNullMatchingPeopleData = matchingPeopleData || {};

  const multipleMatchImportIds: string[] = [];
  const singleMatchImportIds: string[] = [];

  Object.keys(nonNullMatchingPeopleData).forEach((key) => {
    if (nonNullMatchingPeopleData[key].existingData.length > 1) {
      multipleMatchImportIds.push(key);
    } else {
      singleMatchImportIds.push(key);
    }
  });

  const isLoading =
    multipleMatchImportIds.length === 0 && singleMatchImportIds.length === 0 && !errorMessage;

  const saveDisabled =
    multipleMatchImportIds.some((id) => {
      const { importId, existingData, importedData } = nonNullMatchingPeopleData[id];
      const resolution = resolutions[importId];
      if (!resolution) {
        return true;
      }
      if (resolution.createNewEntry) {
        return false;
      }
      const { selectedContactId } = resolution;
      const selectedContact = existingData.find((ed) => ed.contactId === selectedContactId);
      if (!selectedContact) {
        return true;
      }

      return hasDataDiscrepancy(selectedContact, importedData) && !resolution.conflictResolution;
    }) ||
    singleMatchImportIds.some(
      (id) =>
        hasDataDiscrepancy(
          nonNullMatchingPeopleData[id].existingData[0],
          nonNullMatchingPeopleData[id].importedData,
        ) && !resolutions[nonNullMatchingPeopleData[id].importId],
    ) ||
    Boolean(errorMessage) ||
    isLoading;

  const { singleMatchDataDiscrepancyRows, singleMatchCorrectRows } = singleMatchImportIds.reduce(
    (prev, key) => {
      const { importedData, existingData } = nonNullMatchingPeopleData[key];
      const [firstMatch] = existingData;

      if (hasDataDiscrepancy(firstMatch, importedData)) {
        return {
          ...prev,
          ...{
            singleMatchDataDiscrepancyRows: prev.singleMatchDataDiscrepancyRows.concat(key),
          },
        };
      }
      return {
        ...prev,
        ...{
          singleMatchCorrectRows: prev.singleMatchCorrectRows.concat(key),
        },
      };
    },
    {
      singleMatchDataDiscrepancyRows: [] as Array<string>,
      singleMatchCorrectRows: [] as Array<string>,
    },
  );

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">{title}</span>
        <p className="typo-subtitle">{description}</p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {isLoading ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {multipleMatchImportIds.length > 0 ? (
          <>
            <span className="typo-h6">People who match multiple contacts in Team Engine</span>
            <p className="typo-subtitle">
              This is a list of people who match multiple contacts (Employees, Applicants,
              Referrals) in Team Engine. You will need to let us know which contact should be used
              during the import process
            </p>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Imported Employee Name</TableHeaderCell>
                  <TableHeaderCell>Contact Type</TableHeaderCell>
                  <TableHeaderCell>Contact</TableHeaderCell>
                  <TableHeaderCell>Data Discrepancy</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {multipleMatchImportIds.map((key) => {
                  const { importId, importedData, existingData } = nonNullMatchingPeopleData[key];
                  const [firstMatch] = existingData;
                  const otherMatches = existingData.slice(1);

                  const resolution = resolutions[importId] || {};

                  return (
                    <React.Fragment key={`partial-row-${importId}`}>
                      <TableRow key={`partial-first-row-${importId}`}>
                        <TableCell rowSpan={existingData.length + 1}>
                          {employeeNameFromSimpleContact(importedData)}
                        </TableCell>
                        <MatchingContactRow
                          key={firstMatch.contactId}
                          importId={importId}
                          resolution={resolution}
                          existingContact={firstMatch}
                          importedData={importedData}
                          handleRadioButtonSelected={handleRadioButtonSelected}
                          handleResolveDiscrepancy={handleResolveDiscrepancy}
                          clearResolution={clearResolution}
                          includeTableRow={false}
                        />
                      </TableRow>
                      {otherMatches.map((existingContact) => (
                        <MatchingContactRow
                          key={existingContact.contactId}
                          importId={importId}
                          resolution={resolution}
                          existingContact={existingContact}
                          importedData={importedData}
                          handleRadioButtonSelected={handleRadioButtonSelected}
                          handleResolveDiscrepancy={handleResolveDiscrepancy}
                          clearResolution={clearResolution}
                          includeTableRow
                        />
                      ))}
                      <TableRow>
                        <TableCell colSpan={3}>
                          <div className="u-flex-center">
                            <Radio
                              checked={Boolean(resolution.createNewEntry)}
                              onChange={handleRadioButtonSelected(importId, null, null, true)}
                              value="a"
                            />
                            Create new employee entry
                          </div>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </>
        ) : null}

        {singleMatchImportIds.length > 0 ? (
          <>
            <span className="typo-h6">People who match one contact</span>
            <p className="typo-subtitle">
              The following people matched only one contact in Team Engine. You need to only resolve
              any discrepancy in data between our record and the import data.
            </p>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Contact Type</TableHeaderCell>
                  <TableHeaderCell>Data Discrepancy</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {singleMatchDataDiscrepancyRows.map((key) => {
                  const { importId, importedData, existingData } = nonNullMatchingPeopleData[key];
                  const [firstMatch] = existingData;

                  return (
                    <TableRow key={key}>
                      <TableCell>{employeeNameFromSimpleContact(importedData)}</TableCell>
                      <TableCell>
                        <ContactType context={firstMatch} />
                      </TableCell>
                      <TableCell>
                        <div className="u-flex-center">
                          {hasDataDiscrepancy(firstMatch, importedData) &&
                          !resolutions[importId] ? (
                            <UiButton
                              size="xs"
                              onClick={handleResolveDiscrepancy(importId, importedData, firstMatch)}
                            >
                              <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} size="sm" />
                              <span className="u-padding-left-sm">Resolve</span>
                            </UiButton>
                          ) : (
                            <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                          )}
                          {resolutions[importId] ? (
                            <UiButton
                              size="xxs"
                              variant="tertiary"
                              onClick={clearResolution(importId)}
                            >
                              Undo
                            </UiButton>
                          ) : (
                            ''
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {singleMatchCorrectRows.length && !limitRows
                  ? singleMatchCorrectRows.map((key) => {
                      const { importId, importedData, existingData } =
                        nonNullMatchingPeopleData[key];
                      const [firstMatch] = existingData;

                      return (
                        <TableRow key={key}>
                          <TableCell>{employeeNameFromSimpleContact(importedData)}</TableCell>
                          <TableCell>
                            <ContactType context={firstMatch} />
                          </TableCell>
                          <TableCell>
                            <div className="u-flex-center">
                              {hasDataDiscrepancy(firstMatch, importedData) &&
                              !resolutions[importId] ? (
                                <UiButton
                                  size="xs"
                                  onClick={handleResolveDiscrepancy(
                                    importId,
                                    importedData,
                                    firstMatch,
                                  )}
                                >
                                  <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} size="sm" />
                                  <span className="u-padding-left-sm">Resolve</span>
                                </UiButton>
                              ) : (
                                <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                              )}
                              {resolutions[importId] ? (
                                <UiButton
                                  size="xxs"
                                  variant="tertiary"
                                  onClick={clearResolution(importId)}
                                >
                                  Undo
                                </UiButton>
                              ) : (
                                ''
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : null}
                {singleMatchCorrectRows.length && limitRows ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      {limitRows ? (
                        <div
                          className="typo-link"
                          role="presentation"
                          onClick={handleClickViewMoreRows}
                        >
                          Matched {singleMatchCorrectRows.length} rows with no issues. View them
                          now...
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </>
        ) : null}
        {rowToResolve ? (
          <SimpleContactComparisonDialog
            row={rowToResolve}
            onClose={closeResolveDiscrepancyDialog}
          />
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <Tooltip title="Always rehire employees regardless of other conditions. Use this to make the directory look exactly like the uploaded sheet.">
          <FormControlLabel
            label="Force rehire employees?"
            control={
              <Checkbox checked={shouldForceRehire} onChange={handleForceRehireCheckboxChange} />
            }
          />
        </Tooltip>
        <div className="u-display-flex u-gap-10px">
          <UiButton variant="dismiss" onClick={onCancel}>
            Cancel
          </UiButton>
          <UiButton
            variant="primary"
            onClick={handleOnNext}
            disabled={saveDisabled}
            loading={isSaving}
          >
            Save
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
